import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { auth, db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { setDevUser, clearDevUser, devLog, devError, devWarn } from '../utils/debugLogger';
import { migrateHabitsWithStreaks } from '../utils/streakMigration';
import { 
  calculateStreakOnComplete, 
  calculateStreakOnUncomplete, 
  initializeStreakFields,
  recalculateStreakFromCompletions 
} from '../utils/streakUtils';
import { checkStreakMilestone } from '../utils/celebrationUtils';
import { isNativePlatform, isPluginAvailable } from '../utils/platform';
import { 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  updateProfile,
  signInWithCredential
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc,
  updateDoc,
  arrayUnion,
  onSnapshot,
  runTransaction
} from 'firebase/firestore';
import { saveGoogleCalendarColors } from '../config/calendarColors';

// Helper to sanitize objects for Firestore (removes undefined/null/circular refs)
const sanitizeForFirestore = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const sanitized = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      // Skip undefined and null values
      if (value !== undefined && value !== null) {
        // For nested objects, recursively sanitize
        if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
          sanitized[key] = sanitizeForFirestore(value);
        } else if (Array.isArray(value)) {
          // Keep arrays as-is but filter undefined elements
          sanitized[key] = value.filter(v => v !== undefined && v !== null);
        } else {
          sanitized[key] = value;
        }
      }
    }
  }
  return sanitized;
};

// Helper to convert local event to Google Calendar format
const formatEventForGoogle = (event) => {
  const googleEvent = {
    summary: event.name,
    description: event.description || '',
    location: event.location || '',
  };

  // Use startDate if available, fallback to date
  const eventDate = event.startDate || event.date;
  const endDate = event.endDate || eventDate;
  
  if (!eventDate) return null;

  // Handle date/time
  if (event.startTime && event.endTime) {
    googleEvent.start = {
      dateTime: `${eventDate}T${event.startTime}:00`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    googleEvent.end = {
      dateTime: `${endDate}T${event.endTime}:00`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  } else if (event.startTime) {
    googleEvent.start = {
      dateTime: `${eventDate}T${event.startTime}:00`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    // Safely split startTime - guard against undefined/null
    try {
      const timeParts = event.startTime.split(':');
      if (timeParts.length >= 2) {
        const hours = timeParts[0];
        const minutes = timeParts[1];
        const endHour = (parseInt(hours) + 1) % 24;
        googleEvent.end = {
          dateTime: `${endDate}T${String(endHour).padStart(2, '0')}:${minutes}:00`,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
      } else {
        // If time format is invalid, create 1-hour event
        googleEvent.end = {
          dateTime: `${endDate}T${event.startTime}:00`,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
      }
    } catch (e) {
      devWarn('[formatEventForGoogle] Failed to parse startTime:', event.startTime, e);
      return null;
    }
  } else {
    // All-day event: Google Calendar uses exclusive end dates
    // IMPORTANT: Parse the date string directly to avoid timezone issues
    // new Date("2026-01-14") creates midnight UTC, which in timezones west of UTC
    // will show as the previous day when using getDate()
    googleEvent.start = { date: eventDate };
    
    // Parse endDate string directly to avoid timezone conversion issues
    const [year, month, day] = endDate.split('-').map(Number);
    // Add 1 day for Google's exclusive end date format
    const endDateObj = new Date(Date.UTC(year, month - 1, day + 1));
    const endYear = endDateObj.getUTCFullYear();
    const endMonth = String(endDateObj.getUTCMonth() + 1).padStart(2, '0');
    const endDay = String(endDateObj.getUTCDate()).padStart(2, '0');
    googleEvent.end = { date: `${endYear}-${endMonth}-${endDay}` };
  }
  
  return googleEvent;
};

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

// Get sunrise/sunset times for the user's location (approximation based on date)
const getSunTimes = () => {
  const now = new Date();
  const month = now.getMonth();
  // Approximate sunrise/sunset hours by month (Northern Hemisphere)
  // Summer: sunrise ~6am, sunset ~8pm; Winter: sunrise ~7am, sunset ~5pm
  const sunriseHours = [7, 7, 6.5, 6, 5.5, 5.5, 5.5, 6, 6.5, 7, 7, 7];
  const sunsetHours = [17, 17.5, 18, 19.5, 20, 20.5, 20.5, 20, 19, 18, 17, 16.5];
  return {
    sunrise: sunriseHours[month],
    sunset: sunsetHours[month]
  };
};

export const AppProvider = ({ children }) => {
  // Theme: 'manual', 'system', or 'time'
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem('themeMode') || 'system';
  });
  
  // Manual theme preference (only used when themeMode === 'manual')
  const [manualDark, setManualDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : false;
  });
  
  // Computed isDark based on themeMode
  const [isDark, setIsDark] = useState(() => {
    const mode = localStorage.getItem('themeMode') || 'system';
    if (mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } else if (mode === 'time') {
      const { sunrise, sunset } = getSunTimes();
      const hour = new Date().getHours() + new Date().getMinutes() / 60;
      return hour < sunrise || hour >= sunset;
    }
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : false;
  });
  
  // Update isDark when themeMode changes or on interval for time-based
  useEffect(() => {
    const updateTheme = () => {
      if (themeMode === 'system') {
        setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
      } else if (themeMode === 'time') {
        const { sunrise, sunset } = getSunTimes();
        const hour = new Date().getHours() + new Date().getMinutes() / 60;
        setIsDark(hour < sunrise || hour >= sunset);
      } else {
        setIsDark(manualDark);
      }
    };
    
    updateTheme();
    
    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemChange = (e) => {
      if (themeMode === 'system') {
        setIsDark(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleSystemChange);
    
    // Check time-based theme every minute
    let interval;
    if (themeMode === 'time') {
      interval = setInterval(updateTheme, 60000);
    }
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemChange);
      if (interval) clearInterval(interval);
    };
  }, [themeMode, manualDark]);

  // User & Auth
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Data
  const [habits, setHabits] = useState([]);
  const [completions, setCompletions] = useState({});
  const [templates, setTemplates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [goals, setGoals] = useState([]);
  const [archivedGoals, setArchivedGoals] = useState([]);
  const [events, setEvents] = useState([]);
  const [agentProfile, setAgentProfile] = useState({});
  const [calendars, setCalendars] = useState([]);
  const [googleCalendarEvents, setGoogleCalendarEvents] = useState([]);
  const [googleCalendars, setGoogleCalendars] = useState([]);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [googleTokenRevoked, setGoogleTokenRevoked] = useState(false);
  const [showReconnectPrompt, setShowReconnectPrompt] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(null); // null = unknown, wait for data
  const [dataLoading, setDataLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Network & sync states
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasServerData, setHasServerData] = useState(false);
  const [hasPendingWrites, setHasPendingWrites] = useState(false);
  const [mergeConflict, setMergeConflict] = useState(null); // { field, localValue, serverValue, timestamp }

  // Toggle theme (for manual mode)
  const toggleTheme = useCallback(async () => {
    if (themeMode === 'manual') {
      const newValue = !manualDark;
      setManualDark(newValue);
      localStorage.setItem('theme', newValue ? 'dark' : 'light');
      
      // Save to Firestore if user is logged in
      if (user?.userId) {
        try {
          const userDocRef = doc(db, 'users', user.userId);
          await updateDoc(userDocRef, { themeDark: newValue });
        } catch (error) {
          devError('[Theme] Error saving theme preference:', error);
        }
      }
    }
  }, [themeMode, manualDark, user]);
  
  // Change theme mode
  const changeThemeMode = useCallback(async (mode) => {
    setThemeMode(mode);
    localStorage.setItem('themeMode', mode);
    
    // Save to Firestore if user is logged in
    if (user?.userId) {
      try {
        const userDocRef = doc(db, 'users', user.userId);
        await updateDoc(userDocRef, { themeMode: mode });
      } catch (error) {
        devError('[Theme] Error saving theme mode:', error);
      }
    }
  }, [user]);
  
  // Network status listener
  useEffect(() => {
    let syncTimeout = null;
    
    const handleOnline = () => {
      devLog('[Network] Coming back ONLINE, hasPendingWrites:', hasPendingWrites);
      setIsOnline(true);
      // Show syncing indicator if we have pending writes to sync
      if (hasPendingWrites) {
        devLog('[Network] Has pending writes, setting isSyncing=true');
        setIsSyncing(true);
        
        // Firestore may not always fire a snapshot after reconnecting
        // Set a timeout to clear the syncing indicator after 3 seconds
        // If the real sync confirmation comes before this, it will clear it sooner
        syncTimeout = setTimeout(() => {
          devLog('[Network] Sync timeout - clearing isSyncing after 3s');
          setIsSyncing(false);
          setHasPendingWrites(false);
        }, 3000);
      }
    };
    const handleOffline = () => {
      devLog('[Network] Going OFFLINE');
      setIsOnline(false);
      setIsSyncing(false); // Can't sync when offline
      // Clear any pending timeout
      if (syncTimeout) {
        clearTimeout(syncTimeout);
        syncTimeout = null;
      }
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (syncTimeout) {
        clearTimeout(syncTimeout);
      }
    };
  }, [hasPendingWrites]);

  // Handle redirect result from Google sign-in
  useEffect(() => {
    devLog('[Auth] App loaded, checking for redirect result...');
    const handleRedirectResult = async () => {
      try {
        devLog('[Auth] Calling getRedirectResult...');
        const result = await getRedirectResult(auth);
        devLog('[Auth] getRedirectResult returned:', result ? 'user found' : 'null');
        if (result) {
          devLog('[Auth] Google redirect sign-in successful:', result.user.email);
          
          // Create user doc if needed
          const userDocRef = doc(db, 'users', result.user.uid);
          const docSnap = await getDoc(userDocRef);
          if (!docSnap.exists()) {
            devLog('[Auth] Creating user doc for new user...');
            await setDoc(userDocRef, {
              email: result.user.email?.toLowerCase() || '',
              habits: [],
              completions: {},
              templates: [],
              tasks: [],
              archivedTasks: [],
              goals: [],
              archivedGoals: [],
              events: [],
              calendars: [{ id: 'default', name: 'My Calendar', color: '#22C55E', visible: true, type: 'local' }],
              dailyReminderEnabled: true,
              timezoneOffset: -(new Date().getTimezoneOffset() / 60),
              hasCompletedOnboarding: false,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
          }
        }
      } catch (error) {
        devError('[Auth] Redirect result error:', error.code, error.message);
      }
    };
    
    handleRedirectResult();
  }, []);

  // Listen to auth state changes - this is the primary auth detection
  useEffect(() => {
    console.log('[iOS-DEBUG] 👂 Setting up onAuthStateChanged listener...');
    devLog('[Auth] Setting up onAuthStateChanged listener...');
    
    // iOS WORKAROUND: Add timeout fallback in case onAuthStateChanged doesn't fire
    // This can happen due to Capacitor Firebase plugin conflicts
    let authTimeoutId = null;
    let callbackFired = false;
    
    const handleAuthTimeout = () => {
      if (!callbackFired) {
        console.log('[iOS-DEBUG] ⚠️ Auth timeout - onAuthStateChanged did not fire in 5s, forcing authLoading=false');
        setAuthLoading(false);
        // No user detected via timeout, so set to null
        setUser(null);
      }
    };
    
    // Set 5-second timeout as fallback
    authTimeoutId = setTimeout(handleAuthTimeout, 5000);
    console.log('[iOS-DEBUG] ⏱️ Auth timeout set for 5 seconds');
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      callbackFired = true;
      if (authTimeoutId) {
        clearTimeout(authTimeoutId);
        console.log('[iOS-DEBUG] ⏱️ Auth timeout cleared (callback fired)');
      }
      console.log('[iOS-DEBUG] 🔔 onAuthStateChanged fired:', firebaseUser ? `User: ${firebaseUser.email}` : 'No user (null)');
      devLog('[Auth] onAuthStateChanged fired:', firebaseUser ? firebaseUser.email : 'null');
      if (firebaseUser) {
        devLog('[Auth] User detected, setting user state...');
        // CRITICAL: Set dataLoading=true and hasCompletedOnboarding=null BEFORE setting user
        // This prevents the race condition where App.js re-renders with stale hasCompletedOnboarding=false
        // and redirects to onboarding before Firestore data loads
        console.log('[DEBUG-ONBOARDING] Auth: User detected, immediately setting dataLoading=true, hasCompletedOnboarding=null');
        setDataLoading(true);
        setHasCompletedOnboarding(null);
        // Enable dev-only logging if this user is a dev
        setDevUser(firebaseUser.uid);
        setUser({
          userId: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
          photoURL: firebaseUser.photoURL
        });
        
        // If we just completed a Google sign-in redirect, create user doc if needed
        const pendingGoogleSignIn = localStorage.getItem('pendingGoogleSignIn');
        if (pendingGoogleSignIn) {
          devLog('[Auth] Pending Google sign-in detected, creating user doc...');
          localStorage.removeItem('pendingGoogleSignIn');
          
          try {
            const userDocRef = doc(db, 'users', firebaseUser.uid);
            const docSnap = await getDoc(userDocRef);
            if (!docSnap.exists()) {
              await setDoc(userDocRef, {
                email: firebaseUser.email?.toLowerCase() || '',
                habits: [],
                completions: {},
                templates: [],
                tasks: [],
                archivedTasks: [],
                goals: [],
                archivedGoals: [],
                events: [],
                calendars: [{ id: 'default', name: 'My Calendar', color: '#22C55E', visible: true, type: 'local' }],
                dailyReminderEnabled: true,
                timezoneOffset: -(new Date().getTimezoneOffset() / 60),
                hasCompletedOnboarding: false,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              });
              // Set onboarding state immediately to avoid delay
              setHasCompletedOnboarding(false);
              setCalendars([{ id: 'default', name: 'My Calendar', color: '#22C55E', visible: true, type: 'local' }]);
            } else if (!docSnap.data().email) {
              await setDoc(userDocRef, { email: firebaseUser.email?.toLowerCase() || '' }, { merge: true });
            }
          } catch (error) {
            devError('[Auth] Error creating user doc:', error);
          }
        }
      } else {
        devLog('[Auth] No user detected, setting user to null');
        // Clear dev-only logging status
        clearDevUser();
        setUser(null);
        // Reset data when logged out - clear local state only
        devLog('[Auth] Clearing local state for logout');
        setHabits([]);
        setCompletions({});
        setTemplates([]);
        setTasks([]);
        setArchivedTasks([]);
        setGoals([]);
        setArchivedGoals([]);
        setCalendars([]);
        setEvents([]);
        setAgentProfile({});
        // Clear Google calendar state for UI, but don't touch Firestore
        // googleCalendarConnected will be restored from Firestore on next login
        setGoogleCalendars([]);
        setGoogleCalendarEvents([]);
        // Reset googleCalendarConnected for UI (will restore from Firestore on next login)
        setGoogleCalendarConnected(false);
      }
      console.log('[iOS-DEBUG] ✅ Auth check complete, setting authLoading=false');
      devLog('[Auth] Setting authLoading to false');
      setAuthLoading(false);
    });

    return () => {
      if (authTimeoutId) clearTimeout(authTimeoutId);
      unsubscribe();
    };
  }, []);

  // Listen to user data in Firestore (real-time sync)
  useEffect(() => {
    console.log('[DEBUG-ONBOARDING] Firestore useEffect triggered, user:', user ? user.userId : 'null');
    devLog('[Firestore] useEffect triggered, user:', user ? user.userId : 'null');
    if (!user) {
      console.log('[DEBUG-ONBOARDING] No user - setting hasCompletedOnboarding=false, dataLoading=false');
      devLog('[Firestore] No user, skipping listener setup');
      setDataLoading(false);
      // Reset hasCompletedOnboarding to false for unauthenticated users
      // This prevents infinite loading in App.js which checks hasCompletedOnboarding === null
      setHasCompletedOnboarding(false);
      return;
    }

    // User is present - reset hasCompletedOnboarding to null until we get Firestore data
    // This prevents the race condition where false is set before data loads
    console.log('[iOS-DEBUG] 📦 User present - setting hasCompletedOnboarding=null, dataLoading=true');
    console.log('[DEBUG-ONBOARDING] User present - setting hasCompletedOnboarding=null, dataLoading=true');
    setHasCompletedOnboarding(null);
    
    // User is present - ensure dataLoading is true while we wait for server data
    setDataLoading(true);
    
    console.log('[iOS-DEBUG] 🔗 Setting up Firestore listener for user:', user.userId);
    devLog('[Firestore] Setting up Firestore listener for user:', user.userId);
    const userDocRef = doc(db, 'users', user.userId);
    let hasReceivedServerData = false;
    let snapshotFired = false;
    
    // iOS WORKAROUND: Firestore onSnapshot may not fire on native iOS
    // Add timeout to fetch data manually if snapshot doesn't fire
    let dataTimeoutId = null;
    const handleDataTimeout = async () => {
      if (!snapshotFired) {
        console.log('[iOS-DEBUG] ⚠️ Firestore snapshot timeout - fetching data manually');
        console.log('[iOS-DEBUG] 🔍 auth.currentUser:', auth.currentUser?.email || 'NULL');
        console.log('[iOS-DEBUG] 🔍 user state:', user?.email || 'NULL');
        try {
          console.log('[iOS-DEBUG] 📥 Calling getDoc for:', user.userId);
          const docSnap = await getDoc(userDocRef);
          console.log('[iOS-DEBUG] 📥 getDoc returned, exists:', docSnap.exists());
          if (docSnap.exists()) {
            console.log('[iOS-DEBUG] ✅ Manual fetch successful');
            const data = docSnap.data();
            // Set basic data
            setHabits(data.habits || []);
            setCompletions(data.completions || {});
            setTemplates(data.templates || []);
            setTasks(data.tasks || []);
            setArchivedTasks(data.archivedTasks || []);
            setGoals(data.goals || []);
            setArchivedGoals(data.archivedGoals || []);
            setEvents(data.events || []);
            setAgentProfile(data.agentProfile || {});
            setCalendars(data.calendars || [{ id: 'default', name: 'My Calendar', color: '#22C55E', visible: true, type: 'local' }]);
            setGoogleCalendarConnected(data.googleCalendar?.connected || false);
            setHasCompletedOnboarding(data.hasCompletedOnboarding ?? false);
            console.log('[iOS-DEBUG] ✅ Data loaded via manual fetch, hasCompletedOnboarding:', data.hasCompletedOnboarding);
          } else {
            console.log('[iOS-DEBUG] 📄 No user doc exists, new user');
            setAgentProfile({});
            setHasCompletedOnboarding(false);
          }
          setDataLoading(false);
        } catch (error) {
          console.error('[iOS-DEBUG] ❌ Manual fetch error:', error.code, error.message);
          // If permission denied, it means web Firebase auth isn't synced
          if (error.code === 'permission-denied') {
            console.log('[iOS-DEBUG] ⚠️ Permission denied - web Firebase auth not synced');
          }
          setAgentProfile({});
          setHasCompletedOnboarding(false);
          setDataLoading(false);
        }
      }
    };
    dataTimeoutId = setTimeout(handleDataTimeout, 5000);
    console.log('[iOS-DEBUG] ⏱️ Data timeout set for 5 seconds');
    
    const unsubscribe = onSnapshot(userDocRef, async (docSnap) => {
      snapshotFired = true;
      if (dataTimeoutId) {
        clearTimeout(dataTimeoutId);
        console.log('[iOS-DEBUG] ⏱️ Data timeout cleared (snapshot fired)');
      }
      console.log('[iOS-DEBUG] 📬 Firestore snapshot received! exists:', docSnap.exists());
      devLog('[Firestore] Snapshot received, exists:', docSnap.exists(), 'hasPendingWrites:', docSnap.metadata.hasPendingWrites);
      if (docSnap.exists()) {
        let data = docSnap.data();
        
        // Track if we've received confirmed server data (not just cache)
        // hasPendingWrites: false means this is confirmed data from the server
        const isFromServer = !docSnap.metadata.hasPendingWrites;
        if (isFromServer) {
          hasReceivedServerData = true;
        }
        
        devLog('[Firestore] Raw data from snapshot:', data);
        devLog('[Firestore] Events from Firestore:', data.events);
        if (data.events && data.events.length > 0) {
          devLog('[Firestore] First event details:', {
            id: data.events[0].id,
            name: data.events[0].name,
            googleEventId: data.events[0].googleEventId,
            allKeys: Object.keys(data.events[0])
          });
        }
        
        // Migrate streaks for existing users (runs once per session if needed)
        const rawHabits = data.habits || [];
        const rawCompletions = data.completions || {};
        const { habits: migratedHabits, needsSave } = migrateHabitsWithStreaks(rawHabits, rawCompletions);
        
        if (needsSave && isFromServer) {
          // Save migrated habits with streak data
          devLog('[Firestore] Migrating streak data for existing habits');
          await setDoc(userDocRef, { habits: migratedHabits }, { merge: true });
        }
        
        setHabits(migratedHabits);
        setCompletions(rawCompletions);
        setTemplates(data.templates || []);
        setTasks(data.tasks || []);
        setArchivedTasks(data.archivedTasks || []);
        setGoals(data.goals || []);
        setArchivedGoals(data.archivedGoals || []);
        setEvents(data.events || []);
        setAgentProfile(data.agentProfile || {});
        
        // Load theme preferences from Firestore (with localStorage fallback)
        if (data.themeMode !== undefined) {
          setThemeMode(data.themeMode);
          localStorage.setItem('themeMode', data.themeMode);
        }
        if (data.themeDark !== undefined) {
          setManualDark(data.themeDark);
          localStorage.setItem('theme', data.themeDark ? 'dark' : 'light');
        }
        
        // Set onboarding status - default to true for existing users without the field
        const onboardingValue = data.hasCompletedOnboarding !== false;
        console.log('[DEBUG-ONBOARDING] Firestore data received:', {
          rawValue: data.hasCompletedOnboarding,
          computedValue: onboardingValue,
          isFromServer,
          hasPendingWrites: docSnap.metadata.hasPendingWrites
        });
        setHasCompletedOnboarding(onboardingValue);
        
        // Log after state update callback to see what's actually set
        devLog('[Firestore] setEvents called with:', data.events ? data.events.map(e => ({ id: e.id, name: e.name, googleEventId: e.googleEventId })) : []);
        
        // Load calendars from Firestore, with migration from localStorage
        const storedCalendars = data.calendars || [];
        devLog('[Firestore] Calendars from Firestore:', storedCalendars);
        if (storedCalendars.length === 0) {
          // Only create defaults if we have CONFIRMED server data (not cache)
          // This prevents creating a default calendar based on stale cache
          if (!hasReceivedServerData) {
            devLog('[Firestore] No calendars in cache, waiting for server confirmation before creating defaults');
            // Still set empty array so UI shows something
            setCalendars([]);
          } else {
            // Confirmed from server: no calendars exist
            // Try to migrate from localStorage
            let migratedCalendars = [];
            try {
              const localStorageCalendars = localStorage.getItem('localCalendars');
              if (localStorageCalendars) {
                migratedCalendars = JSON.parse(localStorageCalendars);
                // Ensure all calendars have required fields
                migratedCalendars = migratedCalendars.map(cal => ({
                  ...cal,
                  syncToGoogle: cal.syncToGoogle || false,
                  googleCalendarId: cal.googleCalendarId || null,
                  visible: cal.visible !== undefined ? cal.visible : true,
                  type: cal.type || 'local'
                }));
                // Save to Firestore
                await setDoc(userDocRef, { calendars: migratedCalendars }, { merge: true });
                // Clear localStorage to prevent future migrations
                localStorage.removeItem('localCalendars');
              }
            } catch (e) {
              devWarn('[AppContext] Calendar migration from localStorage failed:', e.message);
            }

            if (migratedCalendars.length === 0) {
              // Create default calendar if none exist
              const defaultCalendar = { id: 'default', name: 'My Calendar', color: '#22C55E', visible: true, type: 'local', syncToGoogle: false, googleCalendarId: null };
              setCalendars([defaultCalendar]);
              await setDoc(userDocRef, { calendars: [defaultCalendar] }, { merge: true });
            } else {
              setCalendars(migratedCalendars);
            }
          }
        } else {
          // Ensure all loaded calendars have required fields (defensive)
          const normalizedCalendars = storedCalendars.map(cal => ({
            ...cal,
            syncToGoogle: cal.syncToGoogle === true, // Explicit boolean conversion
            googleCalendarId: cal.googleCalendarId || null,
            visible: cal.visible !== undefined ? cal.visible : true,
            type: cal.type || 'local'
          }));
          setCalendars(normalizedCalendars);
          
          // Clear localStorage if it still exists (for consistency)
          if (localStorage.getItem('localCalendars')) {
            localStorage.removeItem('localCalendars');
          }
        }
        // Sync Google Calendar connection status from server
        if (data.googleCalendarConnected !== undefined) {
          devLog('[Firestore] Setting googleCalendarConnected from server:', data.googleCalendarConnected);
          setGoogleCalendarConnected(data.googleCalendarConnected);
        } else {
          devLog('[Firestore] No googleCalendarConnected field in Firestore data');
        }
        
        // Check if token was revoked (for showing reconnect prompt)
        devLog('[Firestore] Checking googleTokenRevoked:', data.googleTokenRevoked);
        if (data.googleTokenRevoked) {
          devLog('[Firestore] Token was revoked, checking if should show prompt');
          setGoogleTokenRevoked(true);
          // Check if user dismissed the prompt
          const dismissed = localStorage.getItem('dismissedReconnectPrompt');
          devLog('[Firestore] localStorage dismissed:', dismissed);
          if (!dismissed) {
            devLog('[Firestore] Setting showReconnectPrompt to TRUE');
            setShowReconnectPrompt(true);
          } else {
            devLog('[Firestore] Prompt was dismissed, not showing');
            setShowReconnectPrompt(false);
          }
        } else {
          devLog('[Firestore] Token not revoked, hiding prompt');
          setGoogleTokenRevoked(false);
          setShowReconnectPrompt(false);
        }
        
        // Auto-sync timezone on each app load (handles DST changes)
        if (isFromServer) {
          const currentOffset = -(new Date().getTimezoneOffset() / 60);
          const storedOffset = data.timezoneOffset;
          if (storedOffset !== currentOffset) {
            devLog('[Firestore] Timezone changed, updating from', storedOffset, 'to', currentOffset);
            await setDoc(userDocRef, { timezoneOffset: currentOffset }, { merge: true });
          }
        }
        
        // Track sync status
        if (isFromServer) {
          // Server confirmed the data - sync complete
          devLog('[Firestore] Sync complete. Server updatedAt:', data.updatedAt);
          setHasServerData(true);
          setHasPendingWrites(false);
          setIsSyncing(false);
          hasReceivedServerData = true; // Also set for future cache snapshots
          devLog('[Sync] Server confirmed, setting isSyncing=false, hasPendingWrites=false');
        } else {
          // Cache data with pending writes (local changes waiting to sync)
          setHasPendingWrites(true);
          // Only show syncing indicator during INITIAL load (before first server confirmation)
          // After that, writes are so fast the spinner would just flash annoyingly
          devLog('[Sync] Cache snapshot, hasReceivedServerData:', hasReceivedServerData, 'online:', navigator.onLine);
          // Don't set isSyncing=true after we've already received server data
          // This prevents the spinner from getting stuck
          if (!hasReceivedServerData && navigator.onLine && !isSyncing) {
            setIsSyncing(true);
            devLog('[Sync] Setting isSyncing=true (initial load)');
            // Safety timeout: clear isSyncing after 5 seconds if server confirmation doesn't come
            setTimeout(() => {
              devLog('[Sync] Safety timeout - clearing isSyncing after 5s');
              setIsSyncing(false);
            }, 5000);
          }
        }
        
        // Always stop loading once we have data (cache or server)
        // The sync indicator will show if we're still waiting for server confirmation
        console.log('[iOS-DEBUG] ✅ Data loaded! Setting dataLoading=false');
        setDataLoading(false);
      } else {
        // Document doesn't exist yet (new user or still creating)
        console.log('[DEBUG-ONBOARDING] Document does NOT exist for user:', user.userId);
        devLog('[Firestore] Document does not exist yet for user:', user.userId);
        // For new users, we need to stop loading so they can see the onboarding
        // The document will be created when they complete onboarding or make their first action
        setDataLoading(false);
        // For new users without a document, they haven't completed onboarding
        setHasCompletedOnboarding(false);
        console.log('[DEBUG-ONBOARDING] Set hasCompletedOnboarding=false for new user (no doc)');
      }
    }, (error) => {
      console.error('[iOS-DEBUG] ❌ Firestore listener error:', error);
      devError('Error listening to user data:', error);
      setDataLoading(false);
    });

    return () => {
      if (dataTimeoutId) clearTimeout(dataTimeoutId);
      unsubscribe();
    };
  }, [user]);

  // Save data to Firestore
  const saveData = useCallback(async (updates) => {
    if (!user) return;
    
    setSaving(true);
    try {
      const userDocRef = doc(db, 'users', user.userId);
      
      // Only send the updates, do not merge with potentially stale currentData
      await setDoc(userDocRef, { ...updates, updatedAt: new Date().toISOString() }, { merge: true });
    } catch (error) {
      devError('Error saving data:', error);
    } finally {
      setSaving(false);
    }
  }, [user, habits, completions, templates, tasks, archivedTasks, goals, archivedGoals, events]);

  // Update user data (for settings like onboarding completion)
  const updateUserData = useCallback(async (updates) => {
    if (!user) return;
    
    // Update local state for hasCompletedOnboarding if present
    if (updates.hasCompletedOnboarding !== undefined) {
      setHasCompletedOnboarding(updates.hasCompletedOnboarding);
    }
    
    // Save to Firestore
    await saveData(updates);
  }, [user, saveData]);

  // ============ AUTH FUNCTIONS ============

  // Email/Password Sign Up
  const signUp = async (email, password, displayName) => {
    setAuthError(null);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(result.user, { displayName });
        // Update local user state with displayName (onAuthStateChanged fires before updateProfile completes)
        setUser(prev => prev ? { ...prev, displayName } : prev);
      }
      // Create initial user document
      await setDoc(doc(db, 'users', result.user.uid), {
        email: email.toLowerCase(),
        habits: [],
        completions: {},
        templates: [],
        tasks: [],
        archivedTasks: [],
        goals: [],
        archivedGoals: [],
        events: [],
        calendars: [{ id: 'default', name: 'My Calendar', color: '#22C55E', visible: true, type: 'local' }],
        dailyReminderEnabled: true,
        timezoneOffset: -(new Date().getTimezoneOffset() / 60),
        hasCompletedOnboarding: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      // Set onboarding state immediately to avoid delay
      setHasCompletedOnboarding(false);
      setCalendars([{ id: 'default', name: 'My Calendar', color: '#22C55E', visible: true, type: 'local' }]);
      return { success: true };
    } catch (error) {
      const message = getAuthErrorMessage(error.code);
      setAuthError(message);
      return { success: false, error: message };
    }
  };

  // Email/Password Sign In
  const signIn = async (email, password) => {
    setAuthError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error) {
      const message = getAuthErrorMessage(error.code);
      setAuthError(message);
      return { success: false, error: message };
    }
  };

  // Password Reset
  const resetPassword = async (email) => {
    setAuthError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      const message = getAuthErrorMessage(error.code);
      setAuthError(message);
      return { success: false, error: message };
    }
  };

  // Detect if user is on mobile browser
  const isMobileBrowser = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
  };

  // Native Google Sign In using Capacitor Firebase Auth plugin
  // With skipNativeAuth: true, the plugin only handles OAuth UI
  // We then use the credential with web Firebase SDK
  const signInWithGoogleNative = async () => {
    try {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      console.log('[iOS-DEBUG] 🔑 Starting native Google Sign-In (skipNativeAuth mode)...');
      devLog('[Auth] Starting native Google Sign-In...');
      
      // This opens the native Google Sign-In UI and returns credentials
      // With skipNativeAuth: true, it does NOT call native Firebase Auth
      const result = await FirebaseAuthentication.signInWithGoogle();
      console.log('[iOS-DEBUG] ✅ Native OAuth successful:', result.user?.email);
      console.log('[iOS-DEBUG] 📋 Result has credential:', !!result.credential);
      console.log('[iOS-DEBUG] 📋 Result has idToken:', !!result.credential?.idToken);
      
      // Now sign in to WEB Firebase using the credential
      if (result.credential?.idToken) {
        console.log('[iOS-DEBUG] 🔄 Signing in to web Firebase with credential...');
        console.log('[iOS-DEBUG] 📋 idToken preview:', result.credential.idToken.substring(0, 50) + '...');
        console.log('[iOS-DEBUG] 📋 Auth object exists:', !!auth);
        console.log('[iOS-DEBUG] 📋 Auth app name:', auth?.app?.name);
        console.log('[iOS-DEBUG] 📋 Current user before signIn:', auth?.currentUser?.email || 'none');
        const credential = GoogleAuthProvider.credential(result.credential.idToken);
        console.log('[iOS-DEBUG] 📋 GoogleAuthProvider.credential created');
        
        try {
          // Add timeout to catch if signInWithCredential hangs
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('signInWithCredential timeout after 15s')), 15000)
          );
          
          console.log('[iOS-DEBUG] ⏱️ Calling signInWithCredential with 15s timeout...');
          const webResult = await Promise.race([
            signInWithCredential(auth, credential),
            timeoutPromise
          ]);
          console.log('[iOS-DEBUG] ✅ Web Firebase sign-in successful:', webResult.user?.email);
          devLog('[Auth] Web Firebase sign-in successful');
          // onAuthStateChanged will fire and handle the rest
        } catch (syncError) {
          console.error('[iOS-DEBUG] ❌ Web Firebase sign-in failed:', syncError.code, syncError.message, syncError);
          // If credential is expired/invalid, try popup fallback for web
          if (syncError.message?.includes('timeout')) {
            console.log('[iOS-DEBUG] 🔄 Timeout hit. Trying direct web popup auth...');
            try {
              const provider = new GoogleAuthProvider();
              await signInWithPopup(auth, provider);
              console.log('[iOS-DEBUG] ✅ Web popup auth successful');
              return { success: true };
            } catch (popupError) {
              console.error('[iOS-DEBUG] ❌ Web popup also failed:', popupError);
            }
          }
          throw syncError;
        }
      } else {
        throw new Error('No idToken received from Google Sign-In');
      }
      
      return { success: true };
    } catch (error) {
      console.error('[iOS-DEBUG] ❌ Native Google Sign-In error:', error);
      devError('[Auth] Native Google Sign-In error:', error);
      return { success: false, error: error.message };
    }
  };

  // Google Sign In - uses native auth for iOS/Android, web auth for browser
  const signInWithGoogle = async () => {
    setAuthError(null);
    try {
      // Use native auth for iOS/Android apps
      if (isNativePlatform() && isPluginAvailable('FirebaseAuthentication')) {
        return await signInWithGoogleNative();
      }
      
      // Web fallback: popup for mobile browsers, redirect for desktop
      const provider = new GoogleAuthProvider();
      
      if (isMobileBrowser()) {
        // Mobile: Use popup to avoid "disallowed_useragent" error
        devLog('[Auth] Starting Google Sign-In with popup (mobile)...');
        const result = await signInWithPopup(auth, provider);
        devLog('[Auth] Google Sign-In popup successful:', result.user.email);
        return { success: true };
      } else {
        // Desktop: Use redirect for better UX
        devLog('[Auth] Starting Google Sign-In with redirect (desktop)...');
        await signInWithRedirect(auth, provider);
        // Browser will redirect to Google
        return { success: true };
      }
    } catch (error) {
      devError('[Auth] Google Sign-In error:', error);
      const message = getAuthErrorMessage(error.code);
      setAuthError(message);
      return { success: false, error: message };
    }
  };

  // Sign Out
  const logout = async () => {
    devLog('[Auth] Logging out...');
    try {
      await signOut(auth);
      // Note: Do NOT clear googleCalendarConnected here.
      // The connection status is stored in Firestore and will be 
      // restored from server when user logs back in.
      devLog('[Auth] Logout successful');
    } catch (error) {
      devError('Error signing out:', error);
    }
  };

  // Track if we're currently connecting (to prevent double-clicks)
  const [isConnectingCalendar, setIsConnectingCalendar] = useState(false);

  // Connect Google Calendar (using new backend flow)
  const connectGoogleCalendar = async () => {
    if (isConnectingCalendar) {
      return;
    }
    setIsConnectingCalendar(true);
    try {
      const getUrlFn = httpsCallable(functions, 'getGoogleAuthURL');
      const result = await getUrlFn();
      
      // Open in new tab to avoid disallowed_useragent errors in embedded browsers/PWAs
      // This also works around staging URL issues since the popup handles the redirect
      const authWindow = window.open(result.data.url, '_blank', 'noopener,noreferrer');
      
      if (!authWindow) {
        // Popup blocked - fall back to redirect
        window.location.href = result.data.url;
      }
      
      setIsConnectingCalendar(false);
      return { success: true };
    } catch (error) {
      setIsConnectingCalendar(false);
      return { success: false, error: error.message };
    }
  };

  // Disconnect Google Calendar
  const disconnectGoogleCalendar = useCallback(async () => {
    setGoogleCalendarConnected(false);
    setGoogleCalendars([]);
    setGoogleCalendarEvents([]);
    setGoogleTokenRevoked(false);
    setShowReconnectPrompt(false);
    // Clear the dismissed flag so prompt shows again if they reconnect and revoke again
    localStorage.removeItem('dismissedReconnectPrompt');
    
    if (user) {
        try {
            await updateDoc(doc(db, 'users', user.userId), {
                googleCalendarConnected: false,
                googleTokenRevoked: false
            });
        } catch (e) {
            devError('[AppContext] Failed to update Google Calendar disconnect status:', e.message);
        }
    }
  }, [user]);

  // Dismiss the reconnect prompt (don't show again)
  const dismissReconnectPrompt = useCallback(() => {
    devLog('[AppContext] dismissReconnectPrompt called');
    setShowReconnectPrompt(false);
    localStorage.setItem('dismissedReconnectPrompt', 'true');
    devLog('[AppContext] Set dismissedReconnectPrompt in localStorage');
  }, []);

  // Legacy client-side sync functions removed.
  // Sync is now handled by Firebase Functions and useGoogleCalendar hook.

  // Clear auth error
  const clearAuthError = () => setAuthError(null);

  // ============ HABIT FUNCTIONS ============

  const addHabit = useCallback(async (name, timeOfDay = 'anytime', linkedGoalIds = []) => {
    const habitData = typeof name === 'object' ? name : { name, timeOfDay };
    const goalIds = typeof name === 'object' ? (name.linkedGoalIds || []) : linkedGoalIds;
    
    const newHabit = { 
      id: `h${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, 
      name: habitData.name,
      timeOfDay: habitData.timeOfDay || 'anytime',
      goal: habitData.goal || 30,
      // Initialize streak fields
      currentStreak: 0,
      longestStreak: 0,
      lastCompletedDate: null,
      // Habit-to-goal linking
      linkedGoalIds: goalIds,
      // The user's reason, used by the assistant
      ...(habitData.why ? { why: habitData.why } : {}),
    };
    
    const newHabits = [...habits, newHabit];
    setHabits(newHabits);
    
    // Sync bidirectionally: add habit ID to linked goals
    let updatedGoals = goals;
    if (goalIds.length > 0) {
      updatedGoals = goals.map(g => {
        if (goalIds.includes(g.id)) {
          const existingHabitIds = g.linkedHabitIds || [];
          if (!existingHabitIds.includes(newHabit.id)) {
            return { ...g, linkedHabitIds: [...existingHabitIds, newHabit.id] };
          }
        }
        return g;
      });
      setGoals(updatedGoals);
    }
    
    await saveData({ habits: newHabits, goals: updatedGoals });
    return newHabit;
  }, [habits, goals, saveData]);

  const addHabits = useCallback(async (habitsToAdd) => {
    const newHabitsList = habitsToAdd.map((h, i) => ({
      id: `h${Date.now()}_${i}_${Math.random().toString(36).substr(2, 9)}`,
      name: h.name,
      timeOfDay: h.timeOfDay || 'anytime',
      goal: h.goal || 30,
      // Initialize streak fields
      currentStreak: 0,
      longestStreak: 0,
      lastCompletedDate: null,
      // Habit-to-goal linking
      linkedGoalIds: h.linkedGoalIds || [],
    }));
    
    const newHabits = [...habits, ...newHabitsList];
    setHabits(newHabits);
    
    // Sync bidirectionally for any habits with linked goals
    let updatedGoals = [...goals];
    newHabitsList.forEach(habit => {
      if (habit.linkedGoalIds?.length > 0) {
        updatedGoals = updatedGoals.map(g => {
          if (habit.linkedGoalIds.includes(g.id)) {
            const existingHabitIds = g.linkedHabitIds || [];
            if (!existingHabitIds.includes(habit.id)) {
              return { ...g, linkedHabitIds: [...existingHabitIds, habit.id] };
            }
          }
          return g;
        });
      }
    });
    
    if (updatedGoals !== goals) {
      setGoals(updatedGoals);
    }
    
    await saveData({ habits: newHabits, goals: updatedGoals });
    
    // Return the created habit IDs for linking
    return newHabitsList.map(h => h.id);
  }, [habits, goals, saveData]);

  const updateHabit = useCallback(async (id, updates) => {
    const existingHabit = habits.find(h => h.id === id);
    if (!existingHabit) return;
    
    const oldLinkedGoalIds = existingHabit.linkedGoalIds || [];
    const newLinkedGoalIds = updates.linkedGoalIds || oldLinkedGoalIds;
    
    const newHabits = habits.map(h => h.id === id ? { ...h, ...updates } : h);
    setHabits(newHabits);
    
    // Sync bidirectionally if linkedGoalIds changed
    let updatedGoals = goals;
    if (updates.linkedGoalIds !== undefined) {
      const addedGoalIds = newLinkedGoalIds.filter(gid => !oldLinkedGoalIds.includes(gid));
      const removedGoalIds = oldLinkedGoalIds.filter(gid => !newLinkedGoalIds.includes(gid));
      
      if (addedGoalIds.length > 0 || removedGoalIds.length > 0) {
        updatedGoals = goals.map(g => {
          const existingHabitIds = g.linkedHabitIds || [];
          
          // Add habit to newly linked goals
          if (addedGoalIds.includes(g.id) && !existingHabitIds.includes(id)) {
            return { ...g, linkedHabitIds: [...existingHabitIds, id] };
          }
          
          // Remove habit from unlinked goals
          if (removedGoalIds.includes(g.id)) {
            return { ...g, linkedHabitIds: existingHabitIds.filter(hid => hid !== id) };
          }
          
          return g;
        });
        setGoals(updatedGoals);
      }
    }
    
    await saveData({ habits: newHabits, goals: updatedGoals });
  }, [habits, goals, saveData]);

  // Helper to unlink a habit from all goals (used before delete/archive)
  const unlinkHabitFromGoals = useCallback(async (habitId) => {
    const habit = habits.find(h => h.id === habitId);
    if (!habit || !habit.linkedGoalIds?.length) return goals;
    
    const updatedGoals = goals.map(g => {
      if (habit.linkedGoalIds.includes(g.id)) {
        return { 
          ...g, 
          linkedHabitIds: (g.linkedHabitIds || []).filter(id => id !== habitId) 
        };
      }
      return g;
    });
    
    setGoals(updatedGoals);
    return updatedGoals;
  }, [habits, goals]);

  const deleteHabit = useCallback(async (id, { skipUnlink = false } = {}) => {
    const habitToDelete = habits.find(h => h.id === id);
    
    // Unlink from all goals before deleting (unless skipUnlink is true, meaning it was already done)
    let updatedGoals = goals;
    if (!skipUnlink && habitToDelete?.linkedGoalIds?.length > 0) {
      updatedGoals = goals.map(g => {
        if (habitToDelete.linkedGoalIds.includes(g.id)) {
          return { 
            ...g, 
            linkedHabitIds: (g.linkedHabitIds || []).filter(hid => hid !== id) 
          };
        }
        return g;
      });
      setGoals(updatedGoals);
    }
    
    const newHabits = habits.filter(h => h.id !== id);
    const newCompletions = { ...completions };
    delete newCompletions[id];
    setHabits(newHabits);
    setCompletions(newCompletions);
    await saveData({ habits: newHabits, completions: newCompletions, goals: updatedGoals });
  }, [habits, completions, goals, saveData]);

  const reorderHabits = useCallback(async (newHabitsOrder) => {
    setHabits(newHabitsOrder);
    await saveData({ habits: newHabitsOrder });
  }, [saveData]);

  const toggleCompletion = useCallback(async (habitId, dateString) => {
    // Find the habit to update streak
    const habit = habits.find(h => h.id === habitId);
    if (!habit) {
      devError('[toggleCompletion] Habit not found:', habitId);
      return;
    }

    const newCompletions = { ...completions };
    if (!newCompletions[habitId]) {
      newCompletions[habitId] = {};
    }
    
    const isCompleting = !newCompletions[habitId][dateString];
    newCompletions[habitId] = {
      ...newCompletions[habitId],
      [dateString]: isCompleting
    };

    // Recalculate streak from full completions map (supports retroactive updates)
    const habitWithStreaks = initializeStreakFields(habit);
    const previousStreak = habitWithStreaks.currentStreak;

    const streakUpdate = recalculateStreakFromCompletions(habitId, newCompletions, habitWithStreaks);
    const newStreak = streakUpdate.currentStreak;

    let updatedHabits = habits;

    if (
      streakUpdate.currentStreak !== habitWithStreaks.currentStreak ||
      streakUpdate.longestStreak !== habitWithStreaks.longestStreak ||
      streakUpdate.lastCompletedDate !== habitWithStreaks.lastCompletedDate
    ) {
      updatedHabits = habits.map(h => {
        if (h.id === habitId) {
          return {
            ...h,
            currentStreak: streakUpdate.currentStreak,
            longestStreak: streakUpdate.longestStreak,
            lastCompletedDate: streakUpdate.lastCompletedDate
          };
        }
        return h;
      });
      
      // Optimistic UI update for habits
      setHabits(updatedHabits);
      devLog('[toggleCompletion] Streak updated:', {
        habitId,
        dateString,
        isCompleting,
        currentStreak: streakUpdate.currentStreak,
        longestStreak: streakUpdate.longestStreak
      });
      
      // 100- and 365-day streaks get the full-screen streak card
      if (isCompleting && newStreak > previousStreak) {
        setTimeout(() => checkStreakMilestone(newStreak, previousStreak), 300);
      }
    }

    // Optimistic UI update for completions
    setCompletions(newCompletions);
    
    // Persist to Firestore
    const dataToSave = { completions: newCompletions };
    if (updatedHabits !== habits) {
      dataToSave.habits = updatedHabits;
    }
    await saveData(dataToSave);
  }, [completions, habits, saveData]);

  // ============ TEMPLATE FUNCTIONS ============

  const saveTemplate = useCallback(async (name) => {
    const template = {
      id: `t${Date.now()}`,
      name,
      habits: habits.map(h => ({ name: h.name, goal: h.goal, timeOfDay: h.timeOfDay })),
      createdAt: new Date().toISOString()
    };
    const newTemplates = [...templates, template];
    setTemplates(newTemplates);
    await saveData({ templates: newTemplates });
  }, [habits, templates, saveData]);

  const loadTemplate = useCallback(async (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    const newHabits = [
      ...habits,
      ...template.habits.map((h, i) => ({
        id: `h${Date.now()}_${i}_${Math.random().toString(36).substr(2, 9)}`,
        name: h.name,
        goal: h.goal || 30,
        timeOfDay: h.timeOfDay || 'anytime'
      }))
    ];
    setHabits(newHabits);
    await saveData({ habits: newHabits });
  }, [habits, templates, saveData]);

  const deleteTemplate = useCallback(async (id) => {
    const newTemplates = templates.filter(t => t.id !== id);
    setTemplates(newTemplates);
    await saveData({ templates: newTemplates });
  }, [templates, saveData]);

  // ============ TASK FUNCTIONS ============

  // Helper to get task sync settings from localStorage
  const getTaskSyncInfo = () => {
    try {
      const saved = localStorage.getItem('taskCalendarSettings');
      if (saved) {
        const settings = JSON.parse(saved);
        if (settings.syncToGoogle && settings.googleCalendarId) {
          return { syncEnabled: true, googleCalendarId: settings.googleCalendarId };
        }
      }
    } catch (e) {
      devWarn('[AppContext] Failed to parse taskCalendarSettings:', e.message);
    }
    return { syncEnabled: false, googleCalendarId: null };
  };

  const addTask = useCallback(async (task) => {
    const newTask = { ...task, id: `task${Date.now()}`, completed: false };
    
    // Sync to Google Calendar if enabled
    /*
    const { syncEnabled, googleCalendarId } = getTaskSyncInfo();
    if (syncEnabled && googleAccessToken && newTask.dueDate && !newTask.googleEventId) {
      try {
        const calendarService = new GoogleCalendarService(googleAccessToken);
        const taskEvent = {
          name: newTask.title,
          startDate: newTask.dueDate,
          date: newTask.dueDate,
          description: newTask.description || '',
        };
        const result = await calendarService.createEvent(taskEvent, googleCalendarId);
        newTask.googleEventId = result.id;
      } catch (err) {
      }
    }
    */
    
    const newTasks = [...tasks, newTask];
    setTasks(newTasks);
    await saveData({ tasks: newTasks });
  }, [tasks, saveData]);

  // Batch add multiple tasks at once (avoids race conditions)
  const addTasks = useCallback(async (tasksToAdd) => {
    const newTasks = [
      ...tasks,
      ...tasksToAdd.map((t, i) => ({
        id: `task${Date.now()}_${i}_${Math.random().toString(36).substr(2, 9)}`,
        title: t.title,
        priority: t.priority || 'medium',
        category: t.category || 'general',
        dueDate: t.dueDate || null,
        completed: false,
      }))
    ];
    setTasks(newTasks);
    await saveData({ tasks: newTasks });
  }, [tasks, saveData]);

  const updateTask = useCallback(async (id, updates, { skipGoogleSync = false } = {}) => {
    const taskToUpdate = tasks.find(t => t.id === id);
    const updatedTask = { ...taskToUpdate, ...updates };
    
    // Sync to Google Calendar if enabled and task has googleEventId
    /*
    const { syncEnabled, googleCalendarId } = getTaskSyncInfo();
    if (syncEnabled && googleAccessToken && updatedTask.googleEventId && updatedTask.dueDate && !skipGoogleSync) {
      try {
        const calendarService = new GoogleCalendarService(googleAccessToken);
        const taskEvent = {
          name: updatedTask.title,
          startDate: updatedTask.dueDate,
          date: updatedTask.dueDate,
          description: updatedTask.description || '',
        };
        await calendarService.updateEvent(updatedTask.googleEventId, taskEvent, googleCalendarId);
      } catch (err) {
      }
    }
    */
    
    const newTasks = tasks.map(t => t.id === id ? updatedTask : t);
    setTasks(newTasks);
    await saveData({ tasks: newTasks });
  }, [tasks, saveData]);

  const deleteTask = useCallback(async (id, { skipGoogleSync = false } = {}) => {
    const taskToDelete = tasks.find(t => t.id === id);
    
    // Delete from Google Calendar if synced (unless skipGoogleSync is true - e.g., when item was already deleted in Google)
    if (taskToDelete && !skipGoogleSync) {
      /*
      const { syncEnabled, googleCalendarId } = getTaskSyncInfo();
      if (syncEnabled && googleAccessToken && taskToDelete.googleEventId) {
        try {
          const calendarService = new GoogleCalendarService(googleAccessToken);
          await calendarService.deleteEvent(taskToDelete.googleEventId, googleCalendarId);
        } catch (err) {
        }
      }
      */
    }
    
    const newTasks = tasks.filter(t => t.id !== id);
    setTasks(newTasks);
    await saveData({ tasks: newTasks });
  }, [tasks, saveData]);

  const toggleTask = useCallback(async (id) => {
    const task = tasks.find(t => t.id === id);
    const newCompleted = !task.completed;
    
    // If completing, delete from Google Calendar
    if (newCompleted && task.googleEventId) {
      /*
      const { syncEnabled, googleCalendarId } = getTaskSyncInfo();
      if (syncEnabled && googleAccessToken) {
        try {
          const calendarService = new GoogleCalendarService(googleAccessToken);
          await calendarService.deleteEvent(task.googleEventId, googleCalendarId);
        } catch (err) {
        }
      }
      */
    }
    
    const newTasks = tasks.map(t => t.id === id ? { ...t, completed: newCompleted } : t);
    setTasks(newTasks);
    await saveData({ tasks: newTasks });
  }, [tasks, saveData]);

  const archiveTask = useCallback(async (id) => {
    const taskToArchive = tasks.find(t => t.id === id);
    if (!taskToArchive) return;
    
    const archivedTask = { 
      ...taskToArchive, 
      completed: true, 
      completedAt: new Date().toISOString() 
    };
    
    const newTasks = tasks.filter(t => t.id !== id);
    const newArchivedTasks = [...archivedTasks, archivedTask];
    
    setTasks(newTasks);
    setArchivedTasks(newArchivedTasks);
    await saveData({ tasks: newTasks, archivedTasks: newArchivedTasks });
  }, [tasks, archivedTasks, saveData]);

  const restoreTask = useCallback(async (id, newDueDate) => {
    const taskToRestore = archivedTasks.find(t => t.id === id);
    if (!taskToRestore) return;
    
    const { completedAt, ...taskWithoutCompletedAt } = taskToRestore;
    const restoredTask = { 
      ...taskWithoutCompletedAt, 
      completed: false, 
      dueDate: newDueDate || '' 
    };
    
    const newArchivedTasks = archivedTasks.filter(t => t.id !== id);
    const newTasks = [...tasks, restoredTask];
    
    setTasks(newTasks);
    setArchivedTasks(newArchivedTasks);
    await saveData({ tasks: newTasks, archivedTasks: newArchivedTasks });
  }, [tasks, archivedTasks, saveData]);

  const permanentlyDeleteTask = useCallback(async (id) => {
    const newArchivedTasks = archivedTasks.filter(t => t.id !== id);
    setArchivedTasks(newArchivedTasks);
    await saveData({ archivedTasks: newArchivedTasks });
  }, [archivedTasks, saveData]);

  // ============ GOAL FUNCTIONS ============

  // Helper to get goal sync settings from localStorage
  const getGoalSyncInfo = () => {
    try {
      const saved = localStorage.getItem('goalCalendarSettings');
      if (saved) {
        const settings = JSON.parse(saved);
        if (settings.syncToGoogle && settings.googleCalendarId) {
          return { syncEnabled: true, googleCalendarId: settings.googleCalendarId };
        }
      }
    } catch (e) {
      devWarn('[AppContext] Failed to parse goalCalendarSettings:', e.message);
    }
    return { syncEnabled: false, googleCalendarId: null };
  };

  // Helper to build goal event description with milestones
  const buildGoalDescription = (goal) => {
    let desc = goal.description || '';
    if (goal.milestones?.length > 0) {
      desc += '\n\nMilestones:\n' + goal.milestones.map(m => `${m.completed ? '✓' : '○'} ${m.title}`).join('\n');
    }
    return desc;
  };

  const addGoal = useCallback(async (goal) => {
    const linkedHabitIds = goal.linkedHabitIds || [];
    
    const newGoal = { 
      ...goal, 
      id: `goal${Date.now()}`,
      progress: 0,
      milestones: goal.milestones || [],
      linkedHabitIds: linkedHabitIds,
      createdAt: new Date().toISOString()
    };
    
    // Sync to Google Calendar if enabled
    /*
    const { syncEnabled, googleCalendarId } = getGoalSyncInfo();
    if (syncEnabled && googleAccessToken && newGoal.targetDate && !newGoal.googleEventId) {
      try {
        const calendarService = new GoogleCalendarService(googleAccessToken);
        const goalEvent = {
          name: newGoal.title,
          startDate: newGoal.targetDate,
          date: newGoal.targetDate,
          description: buildGoalDescription(newGoal),
        };
        const result = await calendarService.createEvent(goalEvent, googleCalendarId);
        newGoal.googleEventId = result.id;
      } catch (err) {
      }
    }
    */
    
    const newGoals = [...goals, newGoal];
    setGoals(newGoals);
    
    // Sync bidirectionally: add goal ID to linked habits
    let updatedHabits = habits;
    if (linkedHabitIds.length > 0) {
      updatedHabits = habits.map(h => {
        if (linkedHabitIds.includes(h.id)) {
          const existingGoalIds = h.linkedGoalIds || [];
          if (!existingGoalIds.includes(newGoal.id)) {
            return { ...h, linkedGoalIds: [...existingGoalIds, newGoal.id] };
          }
        }
        return h;
      });
      setHabits(updatedHabits);
    }
    
    await saveData({ goals: newGoals, habits: updatedHabits });
  }, [goals, habits, saveData]);

  const updateGoal = useCallback(async (id, updates, { skipGoogleSync = false } = {}) => {
    const goalToUpdate = goals.find(g => g.id === id);
    if (!goalToUpdate) return;
    
    const oldLinkedHabitIds = goalToUpdate.linkedHabitIds || [];
    const newLinkedHabitIds = updates.linkedHabitIds !== undefined ? updates.linkedHabitIds : oldLinkedHabitIds;
    
    const updatedGoal = { ...goalToUpdate, ...updates };
    
    // Sync to Google Calendar if enabled and goal has googleEventId
    /*
    const { syncEnabled, googleCalendarId } = getGoalSyncInfo();
    if (syncEnabled && googleAccessToken && updatedGoal.googleEventId && updatedGoal.targetDate && !skipGoogleSync) {
      try {
        const calendarService = new GoogleCalendarService(googleAccessToken);
        const goalEvent = {
          name: updatedGoal.title,
          startDate: updatedGoal.targetDate,
          date: updatedGoal.targetDate,
          description: buildGoalDescription(updatedGoal),
        };
        await calendarService.updateEvent(updatedGoal.googleEventId, goalEvent, googleCalendarId);
      } catch (err) {
      }
    }
    */
    
    const newGoals = goals.map(g => g.id === id ? updatedGoal : g);
    setGoals(newGoals);
    
    // Sync bidirectionally if linkedHabitIds changed
    let updatedHabits = habits;
    if (updates.linkedHabitIds !== undefined) {
      const addedHabitIds = newLinkedHabitIds.filter(hid => !oldLinkedHabitIds.includes(hid));
      const removedHabitIds = oldLinkedHabitIds.filter(hid => !newLinkedHabitIds.includes(hid));
      
      if (addedHabitIds.length > 0 || removedHabitIds.length > 0) {
        updatedHabits = habits.map(h => {
          const existingGoalIds = h.linkedGoalIds || [];
          
          // Add goal to newly linked habits
          if (addedHabitIds.includes(h.id) && !existingGoalIds.includes(id)) {
            return { ...h, linkedGoalIds: [...existingGoalIds, id] };
          }
          
          // Remove goal from unlinked habits
          if (removedHabitIds.includes(h.id)) {
            return { ...h, linkedGoalIds: existingGoalIds.filter(gid => gid !== id) };
          }
          
          return h;
        });
        setHabits(updatedHabits);
      }
    }
    
    await saveData({ goals: newGoals, habits: updatedHabits });
  }, [goals, habits, saveData]);

  const deleteGoal = useCallback(async (id, { skipGoogleSync = false } = {}) => {
    const goalToDelete = goals.find(g => g.id === id);
    
    // Delete from Google Calendar if synced (unless skipGoogleSync is true - e.g., when item was already deleted in Google)
    if (goalToDelete && !skipGoogleSync) {
      /*
      const { syncEnabled, googleCalendarId } = getGoalSyncInfo();
      if (syncEnabled && googleAccessToken && goalToDelete.googleEventId) {
        try {
          const calendarService = new GoogleCalendarService(googleAccessToken);
          await calendarService.deleteEvent(goalToDelete.googleEventId, googleCalendarId);
        } catch (err) {
        }
      }
      */
    }
    
    // Unlink from all linked habits before deleting
    let updatedHabits = habits;
    if (goalToDelete?.linkedHabitIds?.length > 0) {
      updatedHabits = habits.map(h => {
        if (goalToDelete.linkedHabitIds.includes(h.id)) {
          return { 
            ...h, 
            linkedGoalIds: (h.linkedGoalIds || []).filter(gid => gid !== id) 
          };
        }
        return h;
      });
      setHabits(updatedHabits);
    }
    
    const newGoals = goals.filter(g => g.id !== id);
    setGoals(newGoals);
    await saveData({ goals: newGoals, habits: updatedHabits });
  }, [goals, habits, saveData]);

  const toggleGoalComplete = useCallback(async (id) => {
    const goal = goals.find(g => g.id === id);
    const newCompleted = !goal.completed;
    
    // If completing, delete from Google Calendar
    if (newCompleted && goal.googleEventId) {
      /*
      const { syncEnabled, googleCalendarId } = getGoalSyncInfo();
      if (syncEnabled && googleAccessToken) {
        try {
          const calendarService = new GoogleCalendarService(googleAccessToken);
          await calendarService.deleteEvent(goal.googleEventId, googleCalendarId);
        } catch (err) {
        }
      }
      */
    }
    
    const newGoals = goals.map(g => g.id === id ? { ...g, completed: newCompleted } : g);
    setGoals(newGoals);
    await saveData({ goals: newGoals });
  }, [goals, saveData]);

  const addMilestone = useCallback(async (goalId, title) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;
    const newMilestone = { id: `m${Date.now()}`, title, completed: false };
    const newMilestones = [...(goal.milestones || []), newMilestone];
    const updatedGoal = { ...goal, milestones: newMilestones };
    
    // Update Google Calendar with new milestones
    /*
    const { syncEnabled, googleCalendarId } = getGoalSyncInfo();
    if (syncEnabled && googleAccessToken && goal.googleEventId && goal.targetDate) {
      try {
        const calendarService = new GoogleCalendarService(googleAccessToken);
        const goalEvent = {
          name: goal.title,
          startDate: goal.targetDate,
          date: goal.targetDate,
          description: buildGoalDescription(updatedGoal),
        };
        await calendarService.updateEvent(goal.googleEventId, goalEvent, googleCalendarId);
      } catch (err) {
      }
    }
    */
    
    const newGoals = goals.map(g => 
      g.id === goalId ? { ...g, milestones: newMilestones } : g
    );
    setGoals(newGoals);
    await saveData({ goals: newGoals });
  }, [goals, saveData]);

  const deleteMilestone = useCallback(async (goalId, milestoneId) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;
    const newMilestones = goal.milestones.filter(m => m.id !== milestoneId);
    const completedCount = newMilestones.filter(m => m.completed).length;
    const progress = newMilestones.length > 0 ? Math.round((completedCount / newMilestones.length) * 100) : 0;
    const updatedGoal = { ...goal, milestones: newMilestones, progress };
    
    // Update Google Calendar with new milestones
    /*
    const { syncEnabled, googleCalendarId } = getGoalSyncInfo();
    if (syncEnabled && googleAccessToken && goal.googleEventId && goal.targetDate) {
      try {
        const calendarService = new GoogleCalendarService(googleAccessToken);
        const goalEvent = {
          name: goal.title,
          startDate: goal.targetDate,
          date: goal.targetDate,
          description: buildGoalDescription(updatedGoal),
        };
        await calendarService.updateEvent(goal.googleEventId, goalEvent, googleCalendarId);
      } catch (err) {
      }
    }
    */
    
    const newGoals = goals.map(g => 
      g.id === goalId ? { ...g, milestones: newMilestones, progress } : g
    );
    setGoals(newGoals);
    await saveData({ goals: newGoals });
  }, [goals, saveData]);

  const toggleMilestone = useCallback(async (goalId, milestoneId) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;
    const newMilestones = goal.milestones.map(m => 
      m.id === milestoneId ? { ...m, completed: !m.completed } : m
    );
    const completedCount = newMilestones.filter(m => m.completed).length;
    const progress = Math.round((completedCount / newMilestones.length) * 100);
    const updatedGoal = { ...goal, milestones: newMilestones, progress };
    
    // Update Google Calendar with new milestones
    /*
    const { syncEnabled, googleCalendarId } = getGoalSyncInfo();
    if (syncEnabled && googleAccessToken && goal.googleEventId && goal.targetDate) {
      try {
        const calendarService = new GoogleCalendarService(googleAccessToken);
        const goalEvent = {
          name: goal.title,
          startDate: goal.targetDate,
          date: goal.targetDate,
          description: buildGoalDescription(updatedGoal),
        };
        await calendarService.updateEvent(goal.googleEventId, goalEvent, googleCalendarId);
      } catch (err) {
      }
    }
    */
    
    const newGoals = goals.map(g => 
      g.id === goalId ? { ...g, milestones: newMilestones, progress } : g
    );
    setGoals(newGoals);
    await saveData({ goals: newGoals });
  }, [goals, saveData]);

  const archiveGoal = useCallback(async (id) => {
    const goalToArchive = goals.find(g => g.id === id);
    if (!goalToArchive) return;
    
    // Unlink from all linked habits when archiving
    let updatedHabits = habits;
    if (goalToArchive?.linkedHabitIds?.length > 0) {
      updatedHabits = habits.map(h => {
        if (goalToArchive.linkedHabitIds.includes(h.id)) {
          return { 
            ...h, 
            linkedGoalIds: (h.linkedGoalIds || []).filter(gid => gid !== id) 
          };
        }
        return h;
      });
      setHabits(updatedHabits);
    }
    
    const archivedGoal = { 
      ...goalToArchive, 
      completed: true, 
      completedAt: new Date().toISOString(),
      // Clear links in archived copy
      linkedHabitIds: [],
    };
    
    const newGoals = goals.filter(g => g.id !== id);
    const newArchivedGoals = [...archivedGoals, archivedGoal];
    
    setGoals(newGoals);
    setArchivedGoals(newArchivedGoals);
    await saveData({ goals: newGoals, archivedGoals: newArchivedGoals, habits: updatedHabits });
  }, [goals, archivedGoals, habits, saveData]);

  const restoreGoal = useCallback(async (id, newTargetDate) => {
    const goalToRestore = archivedGoals.find(g => g.id === id);
    if (!goalToRestore) return;
    
    const { completedAt, ...goalWithoutCompletedAt } = goalToRestore;
    const restoredGoal = { 
      ...goalWithoutCompletedAt, 
      completed: false, 
      targetDate: newTargetDate || '' 
    };
    
    const newArchivedGoals = archivedGoals.filter(g => g.id !== id);
    const newGoals = [...goals, restoredGoal];
    
    setGoals(newGoals);
    setArchivedGoals(newArchivedGoals);
    await saveData({ goals: newGoals, archivedGoals: newArchivedGoals });
  }, [goals, archivedGoals, saveData]);

  const permanentlyDeleteGoal = useCallback(async (id) => {
    const newArchivedGoals = archivedGoals.filter(g => g.id !== id);
    setArchivedGoals(newArchivedGoals);
    await saveData({ archivedGoals: newArchivedGoals });
  }, [archivedGoals, saveData]);

  // ============ CALENDAR FUNCTIONS ============

  // Add a new calendar
  const addCalendar = useCallback(async (calendarData) => {
    const newCalendar = {
      id: calendarData.id || `cal-${Date.now()}`,
      name: calendarData.name,
      color: calendarData.color || '#22C55E',
      visible: calendarData.visible !== undefined ? calendarData.visible : true,
      type: 'local',
      syncToGoogle: calendarData.syncToGoogle || false,
      googleCalendarId: calendarData.googleCalendarId || null,
      createdAt: new Date().toISOString()
    };
    const newCalendars = [...calendars, newCalendar];
    setCalendars(newCalendars);
    await saveData({ calendars: newCalendars });
    return newCalendar;
  }, [calendars, saveData]);

  // Update an existing calendar
  const updateCalendar = useCallback(async (calendarId, updates) => {
    const newCalendars = calendars.map(c => 
      c.id === calendarId ? { ...c, ...updates } : c
    );
    setCalendars(newCalendars);
    await saveData({ calendars: newCalendars });
  }, [calendars, saveData]);

  // Delete a calendar
  const deleteCalendar = useCallback(async (calendarId) => {
    if (calendarId === 'default') return; // Don't delete default calendar
    const newCalendars = calendars.filter(c => c.id !== calendarId);
    setCalendars(newCalendars);
    await saveData({ calendars: newCalendars });
  }, [calendars, saveData]);

  // ============ EVENT FUNCTIONS ============

  // Helper to get calendar sync info from Firestore calendars state
  const getCalendarSyncInfo = useCallback((calendarId) => {
    const calendar = calendars.find(c => c.id === calendarId);
    if (calendar && calendar.syncToGoogle) {
      return { 
        syncEnabled: true, 
        googleCalendarId: calendar.googleCalendarId,
        calendarName: calendar.name,
        calendarColor: calendar.color
      };
    }
    return { syncEnabled: false, googleCalendarId: null, calendarName: null, calendarColor: null };
  }, [calendars]);

  // Helper to update calendar with new Google Calendar ID (now uses Firestore)
  const updateLocalCalendarGoogleId = useCallback(async (localCalendarId, googleCalendarId) => {
    await updateCalendar(localCalendarId, { googleCalendarId });
  }, [updateCalendar]);

  const addEvent = useCallback(async (eventData) => {
    const newEvent = {
      id: `e${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: eventData.name,
      date: eventData.date,
      startDate: eventData.startDate || eventData.date,
      endDate: eventData.endDate || eventData.date,
      startTime: eventData.startTime || null,
      endTime: eventData.endTime || null,
      description: eventData.description || '',
      location: eventData.location || '',
      calendarId: eventData.calendarId || 'default',
      color: eventData.color || null,
      googleEventId: null, // Start with null - let sync populate it
      createdAt: new Date().toISOString()
    };
    
    // Check if this calendar syncs to Google
    const { syncEnabled, googleCalendarId, calendarName, calendarColor } = getCalendarSyncInfo(newEvent.calendarId);
    devLog('[AddEvent] Sync check:', { calendarId: newEvent.calendarId, syncEnabled, googleCalendarId });
    
    // If calendar syncs to Google, create the event on Google first
    // This ensures the googleEventId is available before saving locally
    if (syncEnabled) {
      devLog('[AddEvent] Sync enabled, creating Google event...');
      try {
        const googleEvent = formatEventForGoogle(newEvent);
        if (googleEvent) {
          const createGoogleEventFn = httpsCallable(functions, 'createGoogleEvent');
          const result = await createGoogleEventFn({
            calendarId: googleCalendarId,
            event: googleEvent,
            calendarName: `${calendarName} - mind.`,
            calendarColor: calendarColor,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          });
          
          // Only set googleEventId if Google event was successfully created
          devLog('[AddEvent] Google event created, result.data:', result.data);
          newEvent.googleEventId = result.data.id;
          newEvent.googleCalendarId = result.data.calendarIdUsed;
          devLog('[AddEvent] Set googleEventId:', newEvent.googleEventId);
          
          // If a new calendar was created, update local storage
          if (result.data.newCalendarId) {
            updateLocalCalendarGoogleId(newEvent.calendarId, result.data.newCalendarId);
          }
        }
      } catch (err) {
        // Log the error but continue - event will be created locally
        // Next sync will try to push it to Google again
        devError('[AddEvent] Error creating Google event:', err);
      }
    }
    
    // Save event to Firestore (with googleEventId if sync succeeded, null otherwise)
    // Don't update React state directly - let Firestore listener do it
    try {
      const sanitized = sanitizeForFirestore(newEvent);
      devLog('[AddEvent] Saving to Firestore:', { 
        name: sanitized.name, 
        googleEventId: sanitized.googleEventId,
        startTime: sanitized.startTime 
      });
      await saveData({ events: arrayUnion(sanitized) });
      devLog('[AddEvent] Saved successfully');
    } catch (error) {
      devError('[AddEvent] Error saving event:', error);
      throw error;
    }
    
    // Return the event data for immediate use in catch-up sync
    return newEvent;
  }, [saveData]);

  const updateEvent = useCallback(async (id, updates, { skipGoogleSync = false } = {}) => {
    // Simple retry logic for concurrent update conflicts
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const userDocRef = doc(db, 'users', user.userId);
        const userSnap = await getDoc(userDocRef);
        
        if (!userSnap.exists()) return null;
        
        const userData = userSnap.data();
        const allEvents = userData.events || [];
        const eventToUpdate = allEvents.find(e => e.id === id);
        
        if (!eventToUpdate) return null;
        
        // Merge updates and sanitize for Firestore
        const updated = { ...eventToUpdate, ...updates, updatedAt: new Date().toISOString() };
        const sanitized = sanitizeForFirestore(updated);
        
        // Also sanitize all other events in the array to clean up any old null values
        const newEvents = allEvents.map(e => e.id === id ? sanitized : sanitizeForFirestore(e));
        
        // Update Firestore
        await updateDoc(userDocRef, { events: newEvents });
        
        // If we get here, the update succeeded
        // Handle Google sync if needed
        if (!skipGoogleSync) {
          try {
            const { syncEnabled, googleCalendarId } = getCalendarSyncInfo(sanitized.calendarId);
            if (syncEnabled && sanitized.googleEventId && googleCalendarId) {
              try {
                const googleEvent = formatEventForGoogle(sanitized);
                if (googleEvent) {
                  const updateGoogleEventFn = httpsCallable(functions, 'updateGoogleEvent');
                  await updateGoogleEventFn({
                    calendarId: googleCalendarId,
                    eventId: sanitized.googleEventId,
                    event: googleEvent
                  });
                }
              } catch (formatErr) {
                devWarn('[UpdateEvent] Failed to format event for Google:', formatErr);
              }
            }
          } catch (syncErr) {
            devError('[UpdateEvent] Error in Google sync:', syncErr);
          }
        }
        
        return sanitized;
      } catch (err) {
        lastError = err;
        devWarn(`[UpdateEvent] Attempt ${attempt + 1} failed:`, err.message);
        // Wait a bit before retrying
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)));
        }
      }
    }
    
    // All retries failed
    devError('[UpdateEvent] All retries failed:', lastError);
    throw lastError;
  }, [user, getCalendarSyncInfo]);

  const deleteEvent = useCallback(async (id, { skipGoogleSync = false } = {}) => {
    const eventToDelete = events.find(e => e.id === id);
    
    // OPTIMISTIC UPDATE: Remove from state immediately for instant UI feedback
    const newEvents = events.filter(e => e.id !== id);
    setEvents(newEvents);
    
    // Check if this calendar syncs to Google and event has a Google ID
    if (eventToDelete && !skipGoogleSync) {
      const { syncEnabled, googleCalendarId } = getCalendarSyncInfo(eventToDelete.calendarId);
      if (syncEnabled && eventToDelete.googleEventId) {
        try {
          const deleteGoogleEventFn = httpsCallable(functions, 'deleteGoogleEvent');
          await deleteGoogleEventFn({
            calendarId: googleCalendarId,
            eventId: eventToDelete.googleEventId
          });
        } catch (err) {
        }
      }
    }
    
    // Save to Firestore (state already updated above)
    await saveData({ events: newEvents });
  }, [events, saveData, getCalendarSyncInfo]);

  // ============ COMPUTED STATS (Single Source of Truth) ============
  
  const stats = useMemo(() => {
    // Helper to format date as YYYY-MM-DD in local timezone
    const formatDateString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const today = new Date();
    const todayString = formatDateString(today);
    
    // Habit progress
    const completedToday = habits.filter(h => completions[h.id]?.[todayString]).length;
    const habitProgress = habits.length > 0 ? Math.round((completedToday / habits.length) * 100) : 0;
    
    // All active tasks (not completed)
    const activeTasks = tasks.filter(t => !t.completed);
    const activeTaskCount = activeTasks.length;
    
    // Tasks due today (not completed)
    const tasksDueToday = tasks.filter(t => t.dueDate === todayString && !t.completed);
    const tasksDue = tasksDueToday.length;
    
    // Overdue tasks
    const overdueTasks = tasks.filter(t => t.dueDate && t.dueDate < todayString && !t.completed);
    const overdueCount = overdueTasks.length;
    
    // Active goals
    const activeGoalsList = goals.filter(g => !g.completed);
    const activeGoals = activeGoalsList.length;
    
    // Events today (local + Google Calendar)
    // Check multiple possible date fields for compatibility
    const getEventDate = (e) => {
      // For all-day events: use date or startDate
      // For timed events: extract date from start or startDate
      if (e.date) return e.date;
      if (e.startDate) return e.startDate.split('T')[0];
      if (e.start) return e.start.split('T')[0];
      return null;
    };
    
    // Build set of synced event IDs to filter out duplicates
    // (tasks, goals, and events that were synced to Google Calendar)
    const syncedEventIds = new Set([
      ...(tasks || []).filter(t => t.googleEventId).map(t => t.googleEventId),
      ...(goals || []).filter(g => g.googleEventId).map(g => g.googleEventId),
      ...(events || []).filter(e => e.googleEventId).map(e => e.googleEventId)
    ]);
    
    // Filter out synced events from Google Calendar to avoid double-counting
    const filteredGoogleEvents = googleCalendarEvents.filter(e => !syncedEventIds.has(e.id));
    
    const localEventsToday = events.filter(e => getEventDate(e) === todayString).length;
    const googleEventsToday = filteredGoogleEvents.filter(e => getEventDate(e) === todayString).length;
    const eventsToday = localEventsToday + googleEventsToday;
    
    // Streak calculation
    let streak = 0;
    if (habits.length > 0) {
      const checkDate = new Date(today);
      const todayComplete = habits.every(h => completions[h.id]?.[todayString]);
      if (!todayComplete) {
        checkDate.setDate(checkDate.getDate() - 1);
      }
      for (let i = 0; i < 365; i++) {
        const dateStr = formatDateString(checkDate);
        if (habits.every(h => completions[h.id]?.[dateStr])) {
          streak++;
          checkDate.setDate(checkDate.getDate() - 1);
        } else {
          break;
        }
      }
    }
    
    // Goals due today (target date or milestone due today)
    const goalsDueToday = goals.filter(g => {
      if (g.completed) return false;
      if (g.targetDate === todayString) return true;
      if (g.milestones?.some(m => m.dueDate === todayString && !m.completed)) return true;
      return false;
    });
    const goalsDue = goalsDueToday.length;
    
    // Total items for today (events + tasks + goals) - used in Navigation and Dashboard
    const todayItemCount = eventsToday + tasksDue + goalsDue;
    
    return {
      // Today's date string (useful for components)
      todayString,
      // Habits
      habitProgress,
      completedHabitsToday: completedToday,
      totalHabits: habits.length,
      streak,
      // Tasks
      activeTaskCount,
      activeTasks,
      tasksDue,
      tasksDueToday,
      overdueCount,
      overdueTasks,
      // Goals
      activeGoals,
      activeGoalsList,
      goalsDue,
      goalsDueToday,
      // Events
      eventsToday,
      localEventsToday,
      googleEventsToday,
      // Combined today count
      todayItemCount,
    };
  }, [habits, completions, tasks, goals, events, googleCalendarEvents]);

  const value = {
    // Theme
    isDark,
    themeMode,
    toggleTheme,
    changeThemeMode,
    // Auth
    user,
    authLoading,
    authError,
    clearAuthError,
    signUp,
    signIn,
    resetPassword,
    signInWithGoogle,
    logout,
    // Loading states
    dataLoading,
    saving,
    // Onboarding
    hasCompletedOnboarding,
    updateUserData,
    // Assistant
    agentProfile,
    // Habits
    habits,
    completions,
    addHabit,
    addHabits,
    updateHabit,
    deleteHabit,
    reorderHabits,
    toggleCompletion,
    unlinkHabitFromGoals,
    // Templates
    templates,
    saveTemplate,
    loadTemplate,
    deleteTemplate,
    // Tasks
    tasks,
    archivedTasks,
    addTask,
    addTasks,
    updateTask,
    deleteTask,
    toggleTask,
    archiveTask,
    restoreTask,
    permanentlyDeleteTask,
    // Goals
    goals,
    archivedGoals,
    addGoal,
    updateGoal,
    deleteGoal,
    toggleGoalComplete,
    addMilestone,
    deleteMilestone,
    toggleMilestone,
    archiveGoal,
    restoreGoal,
    permanentlyDeleteGoal,
    // Events
    events,
    addEvent,
    updateEvent,
    deleteEvent,
    // Calendars (Firestore-based)
    calendars,
    addCalendar,
    updateCalendar,
    deleteCalendar,
    // Google Calendar
    googleCalendarConnected,
    googleCalendars,
    setGoogleCalendars,
    googleCalendarEvents,
    setGoogleCalendarEvents,
    connectGoogleCalendar,
    disconnectGoogleCalendar,
    isConnectingCalendar,
    // Google token revocation handling
    googleTokenRevoked,
    showReconnectPrompt,
    dismissReconnectPrompt,
    // Computed stats (single source of truth)
    stats,
    // Network & sync status
    isOnline,
    isSyncing,
    hasServerData,
    hasPendingWrites,
    mergeConflict,
    dismissMergeConflict: () => setMergeConflict(null),
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Helper function for auth error messages
function getAuthErrorMessage(code) {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in instead.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.';
    default:
      return 'An error occurred. Please try again.';
  }
}
